import {
  APIComponentAttributes,
  APIComponentType,
  APIFieldAttributes,
  APIFieldType,
  APIOrganizationUser,
  APIReferenceType,
  ArdoqId,
  DiffMode,
  FilterInterfaceFilter,
  ReferenceDirection,
} from '@ardoq/api-types';
import { DropdownItem, Notifier } from '@ardoq/dropdown-menu';
import {
  ChartComponentProperties,
  ChartDimensions,
  ChartWindowState,
  HasViewInstanceId,
  Margin,
  NumericRange,
  NumericScalingFunction,
  Rect,
  Size,
} from '@ardoq/graph';
import { IconName } from '@ardoq/icons';
import { Point } from '@ardoq/math';
import { DisabledZoomControls, DropdownConfig } from '@ardoq/view-settings';
import {
  ActiveConditionalFormattingForLegend,
  ComponentTypeForLegend,
} from '@ardoq/view-legend';
import { JSX, MouseEvent, ReactNode } from 'react';
import {
  GetCssClassNamesOption,
  OnViewSettingsUpdate,
  RepresentationData,
  TextAreaFormat,
} from '@ardoq/data-model';
import { CidArgs } from '@ardoq/common-helpers';
import { type I18nLocale } from '@ardoq/i18n';
import { Locale } from '@ardoq/locale';

export enum BubbleChartGridMode {
  NONE = 'none',
  GRID = 'grid',
  TIME = 'time',
  CUSTOM = 'custom',
}

export enum BubbleChartSelectedFieldType {
  REFERENCE,
  REFERENCED_COMPONENT,
  BUBBLE_COMPONENT,
}

export enum Quadrant {
  TOP_LEFT = 'topLeft',
  TOP_RIGHT = 'topRight',
  BOTTOM_LEFT = 'bottomLeft',
  BOTTOM_RIGHT = 'bottomRight',
}

export const FieldSource = {
  BY_COMPONENT: 'byComponent',
  BY_REFERENCE: 'byReference',
} as const;

export type BubbleChartSelectedFieldInfo = {
  fieldName: string;
  type: BubbleChartSelectedFieldType;
};

export interface BubbleChartQuadrantSettings {
  /** X-axis minimum; the vertical quadrant divider is drawn at the midpoint between this and `xMaximum`. */
  xMinimum: number;
  /** X-axis maximum. */
  xMaximum: number;
  /** Y-axis minimum; the horizontal quadrant divider is drawn at the midpoint between this and `yMaximum`. */
  yMinimum: number;
  /** Y-axis maximum. */
  yMaximum: number;
  /** The four quadrant labels, in order: top-left, top-right, bottom-left, bottom-right. */
  labels: [string, string, string, string];
}

export type IncludeDescendants = 'none' | 'direct' | 'all';

export type LabelFilter = {
  /** Which plotted dimension the range applies to: the X, Y, or bubble-size field. */
  dimension: FieldInfoKey;
  /** The field backing the chosen dimension. */
  fieldName: string | BubbleChartSelectedFieldInfo;
  /** Inclusive lower bound; only bubbles whose value is at least this are labelled. */
  min: number;
  /** Inclusive upper bound; only bubbles whose value is at most this are labelled. */
  max: number;
};

export type BubbleChartViewSettings = {
  /**
   * The chart background. `none` and `grid` only control whether gridlines are
   * drawn behind the bubbles. `time` fixes both axes to a 1–5 range with
   * quadrant lines at the midpoints and a label in each quadrant. `custom` draws
   * the quadrant lines from `customQuadrantSettings`.
   */
  background: BubbleChartGridMode;
  selectedFieldNameX: string | BubbleChartSelectedFieldInfo | null;
  selectedFieldNameY: string | BubbleChartSelectedFieldInfo | null;
  // scaled by area, not radius, so this is effectively the "area" field.
  selectedFieldNameRadius: string | BubbleChartSelectedFieldInfo | null;
  /** Axis bounds and quadrant labels used when `background` is `custom`. */
  customQuadrantSettings: BubbleChartQuadrantSettings;
  // for backwards compatibility, true means show all descendants and false means show direct children
  includeAllDescendants: boolean | IncludeDescendants;
  /**
   * Multiplier applied to every bubble's area, scaling all bubbles up or down
   * together (labels scale with their bubble).
   */
  bubbleAreaScaleFactor: number;
  // The reference bubble chart (`fieldSource: 'byReference'`, `selectedReferenceTypeName`,
  // `selectedReferenceDirection`, and the `BubbleChartSelectedFieldInfo` variant of the axis
  // fields) is behind a temporary feature flag that is not enabled for users, so it is left
  // out of the AI schema descriptions: `SettableBubbleChartViewSettings` omits these fields
  // and narrows the axis fields to `string | null`.
  fieldSource: 'byComponent' | 'byReference';
  selectedReferenceTypeName: string | null;
  selectedReferenceDirection: ReferenceDirection;
  /**
   * Component type ids the axis fields are read from when `fieldSource` is
   * `byComponent`. `['*']` means every type in `selectedComponentTypes`, and
   * `null` means no source of values has been picked.
   */
  valueSourceComponentTypes: string[] | null;
  /**
   * Whether to plot bubbles whose size-field value is 0. Treated as `true` when
   * unset.
   */
  showBubblesWithZeroValue: boolean;
  /**
   * Component type ids to plot. `['*']` includes every type and is the default;
   * emit it when the user asks for all types. Otherwise match what the user
   * asks for against `name` in viewState.bubbleChart.componentTypesById and
   * emit that entry's `ids` unchanged: one name can cover several ids, and only
   * components of the listed types are shown.
   */
  selectedComponentTypes: string[];
  /**
   * The quadrant the chart is zoomed into, or `null`/absent for the whole chart.
   */
  selectedQuadrant?: Quadrant | null;
  /**
   * Whether the legend is shown. The legend is a panel that acts as a key to
   * the visualization, listing the component and reference types in the view
   * (with their colours) and any conditional formatting. Hidden by default; set
   * `true` to show it.
   */
  isLegendActive: boolean;
  // `labelFilter.fieldName` can be a reference field (`BubbleChartSelectedFieldInfo`)
  // at runtime; the settable type narrows it to a component field name (`string`),
  // since the reference bubble chart is not enabled.
  labelFilter?: LabelFilter;
};

/**
 * Keys omitted from {@link SettableBubbleChartViewSettings}: `includeAllDescendants`
 * applies only in workspace mode (in viewpoint mode the plotted set comes from
 * the saved viewpoint), and the reference bubble chart fields are not enabled
 * for users (see the note on {@link BubbleChartViewSettings}).
 */
type IgnoredBubbleChartViewSettingKey =
  | 'includeAllDescendants'
  | 'fieldSource'
  | 'selectedReferenceTypeName'
  | 'selectedReferenceDirection'
  | 'valueSourceComponentTypes';

export type SettableBubbleChartViewSettings = Omit<
  BubbleChartViewSettings,
  | IgnoredBubbleChartViewSettingKey
  | 'selectedFieldNameX'
  | 'selectedFieldNameY'
  | 'selectedFieldNameRadius'
  | 'labelFilter'
> & {
  /**
   * The field on the component type whose value positions each bubble on the X
   * axis, or `null` for none. Pick one from
   * viewState.bubbleChart.componentAxisFieldNames. Include an axis field only
   * when viewState.bubbleChart.canSetComponentAxisFields is true.
   */
  selectedFieldNameX: string | null;
  /**
   * The field on the component type whose value positions each bubble on the Y
   * axis, or `null` for none. Pick one from
   * viewState.bubbleChart.componentAxisFieldNames. Include an axis field only
   * when viewState.bubbleChart.canSetComponentAxisFields is true.
   */
  selectedFieldNameY: string | null;
  /**
   * The field on the component type whose value determines bubble size, or
   * `null` for none. Bubbles are scaled by area, not radius. Pick one from
   * viewState.bubbleChart.componentAxisFieldNames. Include an axis field only
   * when viewState.bubbleChart.canSetComponentAxisFields is true.
   */
  selectedFieldNameRadius: string | null;
  /**
   * Restricts which bubbles are labelled to those within a value range on one
   * dimension. `null` removes it. Absent keeps the current filter, except when
   * the same call sets any axis field — assigning an axis always clears it.
   *
   * Include this only when viewState.bubbleChart.canSetLabelFilter is true.
   * Take `dimension` and `fieldName` from the matching entry in
   * viewState.bubbleChart.labelFilterTargets, and set `min` and `max` within
   * that entry's `range`. That list describes the axes as they are configured
   * now, so do not assign an axis field and filter on it in the same call:
   * assign the field, then filter next.
   */
  labelFilter?: (Omit<LabelFilter, 'fieldName'> & { fieldName: string }) | null;
};

type QuadrantClipaPathIds = {
  topRightQuadrantClipPath: string;
  bottomRightQuadrantClipPath: string;
  bottomLeftQuadrantClipPath: string;
  topLeftQuadrantClipPath: string;
};
type QuadrantLabelTextCoordinates = {
  leftQuadrantsLabelX: number;
  rightQuadrantsLabelX: number;
  topQuadrantsLabelY: number;
  bottomQuadrantsLabelY: number;
};
type QuadrantLabelTexts = {
  topLeftLabelText: string;
  topRightLabelText: string;
  bottomLeftLabelText: string;
  bottomRightLabelText: string;
};
export type QuadrantOverlayProps = {
  chartWidth: number;
  chartHeight: number;
  margin: Margin;
  chartAreaClipPathUrl: string;
  clipPathIds: QuadrantClipaPathIds;
  labelTextCoordinates: QuadrantLabelTextCoordinates;
  labelTexts: QuadrantLabelTexts;
  timeCenterX: number;
  timeCenterY: number;
  selectedQuadrant: Quadrant | null;
};

export type TickMarkProps = {
  transform: string;
  gridLineLength: string;
  tickLabel: string;
  labelOffset: number;
};

export type BubbleChartAxisProps = {
  title: string;
  axisTransform: string;
  axisLinePath: string;
  tickProps: TickMarkProps[];
  axisTitleProps: {
    x: number;
    y: number;
    transform?: string;
  };
};
export type BubbleChartDataPointArgs = {
  data: BubbleChartBubbleModel;
};

export type BubbleChartLabelData = BubbleChartBubbleModel & {
  key?: string;
};

export type BubbleChartBubbleModel = BubbleChartDataPointModel & {
  fontSize: number;
  fontOpacity: number;
  isBlurred: boolean;
  isLabelHidden: boolean;
};

export type BubbleChartDataPointModel = {
  cid: string;
  uniqueId: string;
  x: number;
  y: number;
  radius: number;
  class: string;
  fill?: string;
  stroke?: string;
  label: string;
  isContext: boolean;
};

export type ChartAreaProps = {
  margin: Margin;
  clipPathUrl: string;
  renderedDataPoints: BubbleChartBubbleModel[];
  renderedLabels: BubbleChartLabelData[];
};

// View-internal commands driving the rendering mechanic (measurement, container).
export type BubbleChartRenderingCommands = {
  setClientRect: (size: Size) => void;
  setContentContainer: (
    getBubbleChartContentContainer: () => HTMLDivElement | null
  ) => void;
};

// View-internal commands driving interaction (hover, focus, pan, zoom).
export type BubbleChartInteractionCommands = {
  updateHoverState: (
    hoverState: HighlightState,
    viewInstanceId: string
  ) => void;
  updateFocusState: (
    focusState: HighlightState,
    viewInstanceId: string
  ) => void;
  setCenter: (windowCenter: Point, viewInstanceId: string) => void;
  setScale: (state: ChartWindowState, viewInstanceId: string) => void;
  panSettle: (viewInstanceId: string) => void;
};

// Consumer-supplied integration commands (selection).
export type BubbleChartIntegrationCommands = {
  selectComponent: (args: CidArgs) => void;
};

// Consumer-supplied handlers for user events on the chart.
export type BubbleChartEventHandlers = {
  onBubbleClick: (uniqueId: string) => void;
  onBubbleDoubleClick: (uniqueId: string) => void;
};

export type BubbleChartCommands = BubbleChartInteractionCommands &
  BubbleChartIntegrationCommands &
  BubbleChartEventHandlers;

export type HighlightState = {
  target: SVGElement | null;
  id: string;
  labelPosition: Point | null;
};

export interface BubbleChartScalers {
  fontSize: NumericScalingFunction;
  fontOpacity: NumericScalingFunction;
  radius: NumericScalingFunction;
  unscaleRadius: NumericScalingFunction;
  x: NumericScalingFunction;
  y: NumericScalingFunction;
}

export type BubbleChartZoomControlsProps = {
  zoomIn: () => void;
  zoomCenter: () => void;
  zoomOut: () => void;
  disabledState: DisabledZoomControls;
  selectQuadrantButtonProps: {
    iconName: IconName;
    options: DropdownItem[];
  } | null;
  i18nLocale?: I18nLocale;
};

export type BubbleLegendRow = {
  id: string;
  title: string;
  sections: Array<{
    heading: string | null;
    items: Array<{
      getShape: () => JSX.Element;
      color: string;
      style: React.CSSProperties;
    }>;
  }>;
};

type BubbleChartViewLegendContent = {
  componentTypes: ComponentTypeForLegend[];
  additionalRows?: BubbleLegendRow[];
  activeConditionalFormatting: ActiveConditionalFormattingForLegend;
  activeDiffMode: DiffMode | null;
};
export type BubbleChartViewLegendProps = BubbleChartViewLegendContent & {
  isLegendActive: boolean;
};

export type ChartScaleResult = {
  scalers: BubbleChartScalers;
  visibleRangeX: NumericRange;
  visibleRangeY: NumericRange;
};

export type ChartChrome = {
  xAxisProps: BubbleChartAxisProps;
  yAxisProps: BubbleChartAxisProps;
  quadrantOverlayProps: QuadrantOverlayProps | null;
  zoomControlsProps: BubbleChartZoomControlsProps | null;
  viewLegendContent: BubbleChartViewLegendContent;
};

export type BubbleChartRenderingParameters = ChartScaleResult &
  ChartChrome & {
    chartAreaClipPathUrl: string;
    chartAreaProps: ChartAreaProps;
    sizedDataPoints: BubbleChartBubbleModel[];
    positionedDataPoints: BubbleChartBubbleModel[];
    decollidedLabels: BubbleChartLabelData[];
  };

export type BubbleChartProperties = ChartComponentProperties &
  HasViewInstanceId & {
    hoverState: HighlightState;
    focusState: HighlightState;
    clientRect: Size;
    dimensions: ChartDimensions;
    chartRenderingParameters: BubbleChartRenderingParameters;
    bubbleChartCommands: BubbleChartCommands & BubbleChartRenderingCommands;
  };

type BubbleChartAttributes = ChartWindowState & {
  clientRect: Size;
  margin: Margin;
  dimensions: ChartDimensions;
  domain: BubbleChartDomains;
};

export type BubbleChartDomains = Record<
  'x' | 'y' | 'radius' | 'labeledBubblesRadius',
  NumericRange
>;

export type FieldNameAndLabel = WithPermissionsInfo &
  Pick<APIFieldAttributes, 'name' | 'label'>;
type WithPermissionsInfo = { isConfidential: boolean };

export type FieldData = WithPermissionsInfo & APIFieldAttributes;

export type ComponentTypeRegistryValue = {
  componentType: APIComponentType;
  components: Set<string>;
};

export type ComponentTypeSummary = {
  name: string;
  ids: ArdoqId[];
  representationData: RepresentationData;
  componentCount: number;
  numericFieldCount: number;
};

export type ReferenceTypeSummary = {
  referenceTypeName: string;
  direction: ReferenceDirection;
  /** Names of the component types at the far end of the references. */
  linkedComponentTypeNames: string[];
  numericFieldCount: number;
};

/**
 * Attributes present only in viewpoint mode
 */
type ViewpointModeAttributesBase = {
  componentTypeRegistry: Map<string, ComponentTypeRegistryValue>;
};

export type BubbleChartValueSource =
  | { type: 'none' }
  | { type: 'component'; componentTypeIds: ArdoqId[] }
  | {
      type: 'reference';
      direction: ReferenceDirection;
      referenceTypeName: string;
    };

export type BubbleChartSourceOptions = {
  allComponentsSummary: Pick<
    ComponentTypeSummary,
    'componentCount' | 'numericFieldCount'
  >;
  componentTypeSummaries: ComponentTypeSummary[];
  referenceTypeSummaries: ReferenceTypeSummary[];
};

export type BubbleChartSources = {
  valueSource: BubbleChartValueSource;
  options: BubbleChartSourceOptions;
};

export type BubbleChartViewModelSourceData = HasViewInstanceId & {
  viewSettings: BubbleChartViewSettings;
  isLoadingData: boolean;
  data: BubbleChartDataPointModel[];
  xAxisTitle: string;
  yAxisTitle: string;
  numericFields: FieldData[];
  allNumericFields: FieldData[];
  availableBubbleComponentFields: FieldNameAndLabel[];
  availableReferenceFields: FieldNameAndLabel[];
  availableReferencedComponentFields: FieldNameAndLabel[];
  availableIncomingReferenceTypeNames: string[];
  availableOutgoingReferenceTypeNames: string[];
  viewpointModeAttributes?: ViewpointModeAttributesBase;
  /** Viewpoint mode with the reference bubble chart feature only. */
  sources: BubbleChartSources | null;
  viewInterface: BubbleChartViewInterface;
  activeDiffMode: DiffMode | null;
  isScopeDiffMode: boolean;
  isPresentationMode: boolean;
  popovers: BubbleChartPopovers;
  fieldNotifiers?: BubbleChartFieldNotifiers;
  emptyFields?: SelectedFieldValuePresenceData;
  emptyStateData: BubbleChartEmptyStateData;
  isFilteredByRadius: boolean;
  radiusFieldData: RadiusFieldLegendData;
  locale: Locale;
  i18nLocale: I18nLocale;
  users?: APIOrganizationUser[];
  hasReferenceBubbleChartFeature: boolean;
  /**
   * Optional translated labels for the inline quadrant-zoom dropdown
   * shown in presentation mode and workspace mode (where the settings-bar
   * dropdown is bypassed by `createRenderingParameters` →
   * `getZoomControlsProps`). Pattern A: when omitted, English defaults render.
   */
  selectedQuadrantDropdownLabels?: SelectedQuadrantDropdownLabels;
  /**
   * Optional translated labels for the TIME layout quadrant names
   * (Tolerate, Invest, Migrate, Eliminate) shown in the chart and
   * the settings dropdown. Pattern A: when omitted, English defaults render.
   */
  timeLayoutLabels?: TimeLayoutLabels;
};

export type ComponentTypeSelectLabels = {
  allLabel: string;
  componentTypeLabel: string;
};

export type SelectedQuadrantDropdownLabels = {
  noBackgroundSetUpLabel: string;
  zoomToQuadrantLabel: string;
  showAllLabel: string;
  zoomToTemplate: (quadrantLabel: string) => string;
};

export type TimeLayoutLabels = {
  tolerateLabel: string;
  investLabel: string;
  migrateLabel: string;
  eliminateLabel: string;
};

export type BubbleChartRenderingData = BubbleChartViewModelSourceData &
  BubbleChartAttributes & {
    hoverState: HighlightState;
    focusState: HighlightState;
    viewpointModeAttributes?: ViewpointModeAttributesBase;
    quadrantSettings: BubbleChartQuadrantSettings | null;
    chartRenderingParameters: BubbleChartRenderingParameters | null;
    getBubbleChartContentContainer: () => HTMLDivElement | null;
    selectedQuadrantData: BubbleChartDataPointModel[] | null;
    quadrantZoomState: QuadrantZoomState;
    isPresentationMode: boolean;
    bubbleChartCommands: BubbleChartCommands;
  };

type RenderingDataPropertiesConsumedByView =
  | 'viewInstanceId'
  | 'viewSettings'
  | 'numericFields'
  | 'data'
  | 'windowCenter'
  | 'windowScale'
  | 'hoverState'
  | 'focusState'
  | 'clientRect'
  | 'dimensions'
  | 'chartRenderingParameters'
  | 'popovers'
  | 'emptyStateData';

export type BubbleChartViewProperties = {
  ViewLegendComponent?: React.ComponentType<BubbleChartViewLegendProps>;
  EmptyStateComponent?: React.ComponentType<BubbleChartEmptyStateData>;
  ZoomControlsComponent?: React.ComponentType<BubbleChartZoomControlsProps>;
  viewInterface: BubbleChartViewInterface;
  trackingCallbacks?: BubbleChartTrackingCallbacks;
  bubbleChartCommands: BubbleChartCommands & BubbleChartRenderingCommands;
} & Pick<BubbleChartRenderingData, RenderingDataPropertiesConsumedByView>;

export type BubbleChartEmptyStateData = {
  isViewEmpty: boolean;
  selectedComponentHasNumericFields: boolean;
  hasActiveFilter: boolean;
  allDescendantsIncluded: boolean;
  hasFieldsSelected: boolean;
  isPresentationMode: boolean;
  noNumericFields: boolean;
  isWorkspaceEmpty?: boolean;
  hasOnlyPointsWithZeroRadius?: boolean;
  onChangeFieldsClick?: (event: MouseEvent) => void;
  hasSelectedComponentType?: boolean;
  onChangeBubbleSizeField?: (event: MouseEvent) => void;
  onShowSizeZeroBubbles?: VoidFunction;
};

type BubbleChartPopover = {
  selectBackgroundNotifier?: Notifier;
  popoverContent?: ReactNode;
};

type BubbleChartPopoversNames = 'disabledZoomToQuadrantPopover';

export type BubbleChartPopovers = Record<
  BubbleChartPopoversNames,
  BubbleChartPopover
>;

export type BubbleChartFieldNotifiers = {
  xFieldDropdown: Notifier;
  yFieldDropdown: Notifier;
  bubbleSizeFieldDropdown: Notifier;
};

export type SelectedFieldValuePresenceData = {
  xHasValues: boolean;
  yHasValues: boolean;
  radiusHasValues: boolean;
};

export type QuadrantZoomState = {
  selectedQuadrant: Quadrant | null;
  quadrantWindowStates: Record<Quadrant, ChartWindowState> | null;
};

export type ComponentTypeRegistry = Map<string, ComponentTypeRegistryValue>;

export type ScalingFunction = (value: number) => number;

export type BubbleChartViewSettingsControlsHandlers<T> = {
  /**
   * This function is used to map the state to the source data for your settings controls
   */
  getSettingsControlsData: (
    previousState: BubbleChartRenderingData,
    currentState: BubbleChartRenderingData
  ) => T | null;
  /**
   * This function is used dispatch update to the view settings controls instance
   */
  publishSettingsControlsData: (settingsData: T) => void;
};

export type GetAxisPropsArgs = {
  margin: Margin;
  height: number;
  width: number;
  ticks: number[];
  scaler: (value: number) => number;
  grid: boolean;
  title: string;
  locale: Locale;
};

export interface BubbleLayoutInfo {
  cid: string;
  uniqueId: string;
  center: Point;
  radius: number;
  labelRect: Rect;
  index: number;
  fontSize: number;
}

type FormatArgs = {
  fieldId: ArdoqId;
  value: unknown;
  fieldType?: APIFieldType;
  excludeTime?: boolean;
  textAreaFormat?: TextAreaFormat;
};
type Format = (args: FormatArgs) => string | null;

export type ComponentDisplayColors = { fill?: string; stroke?: string };

export type BubbleChartViewInterface = {
  onViewSettingsUpdate: OnViewSettingsUpdate;
  contextMenuName: string | null;

  get: (componentId: ArdoqId) => APIComponentAttributes | null; // graph interface
  getComponentDisplayName: (componentId: ArdoqId) => string | null; // graph interface
  isComponentIncludedInContextByFilter: (
    component: APIComponentAttributes
  ) => boolean; // graph interface
  getComponentParentId: (componentId: ArdoqId) => ArdoqId | null; // graph interface
  getComponentWorkspaceId: (componentId: ArdoqId) => ArdoqId | null; // graph interface
  getRootComponents: (workspaceId: ArdoqId) => APIComponentAttributes[]; // graph interface
  getComponentType: (componentId: ArdoqId) => APIComponentType | null; // graph interface
  getReferenceTypeId: (referenceId: ArdoqId) => number | null; // graph interface
  getReferenceTypeById: (
    workspaceId: string,
    referenceTypeId: string | number
  ) => APIReferenceType | null; // graph interface

  getComponentCssClassNames: (
    componentId: string,
    options?: GetCssClassNamesOption | undefined
  ) => string | null; // RepresentationInterface
  getComponentDisplayColor: (
    componentId: string,
    options: GetCssClassNamesOption
  ) => ComponentDisplayColors; // RepresentationInterface

  getComponentDisplayLabel: (componentId: string) => string | null; // LabelsInterface
  getComponentFields: (componentId: ArdoqId) => APIFieldAttributes[]; // LabelsInterface
  getComponentFieldValue: (componentId: ArdoqId, fieldName: string) => unknown; // LabelsInterface
  getReferenceRootWorkspaceId: (referenceId: ArdoqId) => ArdoqId | null; // LabelsInterface
  getReferenceFields: (referenceId: string) => APIFieldAttributes[]; // LabelsInterface
  getReferenceFieldValue: (referenceId: ArdoqId, fieldName: string) => unknown; // LabelsInterface
  getFieldByName: (
    fieldName: string,
    options?:
      | {
          includeTemplateFields?: boolean | undefined;
        }
      | undefined
  ) => APIFieldAttributes | null; // LabelsInterface
  getFieldLabel: (fieldId: string) => string | null; // LabelsInterface
  getFieldsOfWorkspace: (
    workspaceId: string,
    fieldTypes: APIFieldType[]
  ) => APIFieldAttributes[]; // LabelsInterface
  formatField: Format;
  getComponentLabelFilter: () => FilterInterfaceFilter;
  getComponentTypeName: (componentId: string) => string | null;

  getComponentLevel: (componentId: string) => number; // view-specific

  trackingCallbacks?: BubbleChartTrackingCallbacks;
};

export type QuadrantInfo = {
  xDirection: 1 | -1;
  yDirection: 1 | -1;
  centerOffsetX: number;
  centerOffsetY: number;
};

export type BubbleRadiusLegendProps = {
  chartData: BubbleChartDataPointModel[];
  radiusDomain: NumericRange;
  radiusScaler: NumericScalingFunction;
  radiusUnscaler: NumericScalingFunction;
  includeZero: boolean;
  locale: Locale;
};

export type LegendBubbleData = { dataValue: number; radius: number };
export type LegendBubbleDataRange = [
  min: LegendBubbleData | null,
  max: LegendBubbleData,
];
export type RadiusFieldLegendData = { label: string; _id: string } | null;

export const FIELD_INFO_KEYS = [
  'selectedFieldNameX',
  'selectedFieldNameY',
  'selectedFieldNameRadius',
] as const;

export type FieldInfoKey = (typeof FIELD_INFO_KEYS)[number];

export const DIMENSION_TO_DATA_KEY = {
  selectedFieldNameX: 'x' as const,
  selectedFieldNameY: 'y' as const,
  selectedFieldNameRadius: 'radius' as const,
} satisfies Record<FieldInfoKey, string>;

export type FieldPickerArgs = {
  fieldName: FieldInfoKey;
  additionalOptions?: DropdownItem[];
} & Omit<
  DropdownConfig,
  'id' | 'type' | 'options' | 'popoverText' | 'isDisabled'
>;

export type BubbleChartTrackingCallbacks = {
  onViewModelReady?: () => void;
  onRendered?: () => void;
};
